import { z } from 'zod';
import * as R from 'remeda';
import { LlmInputMetadata } from '../../interfaces.js';
import { DateTimeSchema } from '../dates.js';
import { objectIdRef, ObjectIdSchema, ObjectIdString, ActivityDataEntityType } from '../common.js';
import { candidateProfileSchemas, LlmResponseSchemaConfig } from '../llmResponseSchemas.js';
import { OpenAiResponseWithUsage, OpenAiUsageShape } from '../lib/openAi.js';

// awaitingClientToolCalls: stream paused for browser tool execution; resumed by createChatLlmResponseWithTools.
// testComplete/testFailed: terminal statuses for Prompt Lab test runs, so production queries filtering
// status 'complete'/'failed' exclude them without needing an extra condition. See testStatusFor.
export const LlmResponseStatus = z.enum([
  'new',
  'pending',
  'awaitingClientToolCalls',
  'complete',
  'failed',
  'testComplete',
  'testFailed',
]);
export type LlmResponseStatus = z.infer<typeof LlmResponseStatus>;

/** Provider-agnostic token usage; the canonical home for cost data on an LlmResponse. */
export const LlmUsageData = z.object({
  inputTokens: z.number(),
  cachedInputTokens: z.number(),
  outputTokens: z.number(),
});
export type LlmUsageData = z.infer<typeof LlmUsageData>;

export function toUsageData(usage: OpenAiUsageShape): LlmUsageData {
  return {
    inputTokens: usage.prompt_tokens,
    cachedInputTokens: usage.prompt_tokens_details?.cached_tokens ?? 0,
    outputTokens: usage.completion_tokens,
  };
}

/** Derives usageData from a stored raw OpenAI response body; null when no usage is present. */
export function usageDataFromRawData(rawData: unknown): LlmUsageData | null {
  const parsed = OpenAiResponseWithUsage.safeParse(rawData);
  return parsed.success ? toUsageData(parsed.data.usage) : null;
}

/** Sums usage across a chat turn's tool-loop iterations and paused/resumed runs. */
export function addUsageData(
  total: LlmUsageData | null,
  added: LlmUsageData | null
): LlmUsageData | null {
  if (!total || !added) {
    return total ?? added;
  }
  return {
    inputTokens: total.inputTokens + added.inputTokens,
    cachedInputTokens: total.cachedInputTokens + added.cachedInputTokens,
    outputTokens: total.outputTokens + added.outputTokens,
  };
}

export const ILlmResponse = z.object({
  _id: ObjectIdSchema,
  account: objectIdRef('Account'),
  prompt: objectIdRef('LlmPrompt'),
  candidate: objectIdRef('Candidate').nullish(),
  entity: ObjectIdSchema.nullish(),
  entityType: ActivityDataEntityType.nullish(),
  opportunity: objectIdRef('Opportunity').nullish(),
  chat: objectIdRef('LlmChat').nullish(),

  provider: z.string(),
  model: z.string(),
  promptType: z.string(),
  status: LlmResponseStatus.default('new'),
  // TODO: remove z.any() here
  params: z.any().optional(),
  inputHash: z.string().optional(),
  templateInput: z.record(z.string(), z.unknown()).optional(),
  rawData: z.unknown().optional(),
  usageData: LlmUsageData.nullish(),
  responseSchema: LlmResponseSchemaConfig.nullable(),
  parsedResponse: z.record(z.string(), z.unknown()).nullish(),
  outputHash: z.string().optional(),
  error: z.unknown().optional(),
  isPreload: z.boolean().default(false),
  cacheHitCount: z.number().default(0),
  metadata: LlmInputMetadata,
  lastCacheHitDate: DateTimeSchema.optional(),
  responseDate: DateTimeSchema.optional(),
  departmentKey: z.string().array().nullish(),

  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type ILlmResponse<ResponseType = Record<string, unknown>> = z.infer<typeof ILlmResponse> & {
  parsedResponse: ResponseType | null;
};

export const LlmResponseDTO = ILlmResponse.omit({
  account: true,
  provider: true,
  model: true,
  responseSchema: true,
  metadata: true,
  _id: true,
}).safeExtend({
  _id: ObjectIdString,
});
export type LlmResponseDTO = z.infer<typeof LlmResponseDTO>;

// TODO: add other llmResponse schemas too
const responseSchemasByPromptType = candidateProfileSchemas;
type PromptTypeWithSchema = keyof typeof responseSchemasByPromptType;
type ParsedResponseForPromptType<T extends PromptTypeWithSchema> = z.infer<
  (typeof responseSchemasByPromptType)[T]
>;

export type LlmResponseDTOForPromptType<T extends PromptTypeWithSchema> =
  T extends PromptTypeWithSchema ?
    Omit<LlmResponseDTO, 'promptType' | 'parsedResponse'> & {
      promptType: T;
      parsedResponse?: ParsedResponseForPromptType<T> | null;
    }
  : never;

export const llmResponseDTOsByPromptType = R.mapValues(
  responseSchemasByPromptType,
  (schema, promptType) => {
    return z.object({
      ...LlmResponseDTO.shape,
      promptType: z.literal(promptType),
      parsedResponse: schema.nullish(),
    });
  }
) as {
  [T in PromptTypeWithSchema]: z.ZodType<LlmResponseDTOForPromptType<T>>;
};

export const AnyCandidateProfileDTO = z.union(
  R.keys(candidateProfileSchemas).map((key) => llmResponseDTOsByPromptType[key])
);
export type AnyCandidateProfileDTO = z.infer<typeof AnyCandidateProfileDTO>;
